export interface HreflangDomainInfo {
    domainUrl: string;
    seoUrl: string;
    locale: string;
    isDefault: boolean;
}

export interface HreflangSalesChannelInformation {
    [key: string]: {
        [key: string]: HreflangDomainInfo;
    };
}

/**
 * A Vue composable to generate hreflang link tags for useHead.
 *
 * @param hreflangData - A Ref containing the hreflang information fetched from the API.
 * @param isHomepage - A boolean indicating whether we are on the homepage.
 * @param isProductPage - A boolean indicating whether we are on the product page.
 * @returns A computed property that returns an array of link objects for SEO.
 */
export function useHreflangLinks(hreflangData: Ref<HreflangSalesChannelInformation | null | undefined>, isHomepage: boolean | undefined = false, isProductPage: boolean | undefined = false) {
    const hreflangLinks = computed(() => {
        // If there's no data, return an empty array immediately.
        if (!hreflangData.value) {
            return [];
        }

        const result: object[] = [];

        // Iterate over the sales channels
        for (const salesChannelDomainsObject of Object.values(hreflangData.value)) {
            // Iterate over the domains within each sales channel
            for (const domain of Object.values(salesChannelDomainsObject)) {
                // Ensure all necessary properties exist before creating a link
                if (domain.domainUrl && domain.seoUrl && domain.locale) {
                    // Construct the full URL, safely removing any trailing slash from the domain
                    // and any leading slash from the seoUrl to prevent double slashes.
                    const cleanDomainUrl = domain.domainUrl.replace(/\/$/, '');
                    const cleanSeoUrl = domain.seoUrl.replace(/^\//, '');
                    const href = `${cleanDomainUrl}/${cleanSeoUrl}`;

                    // Add the standard 'alternate' hreflang link
                    result.push({
                        rel: 'alternate',
                        hreflang: domain.locale,
                        href,
                    });

                    // If this domain is marked as the default, also add the 'x-default' link
                    if (domain.isDefault && !isProductPage) {
                        result.push({
                            rel: 'alternate',
                            hreflang: 'x-default',
                            href,
                        });
                    }
                }

                // Handle homepage
                if (isHomepage && domain.domainUrl && domain.locale) {
                    const cleanDomainUrl = domain.domainUrl.replace(/\/$/, '');
                    const href = `${cleanDomainUrl}/`;

                    // Add the standard 'alternate' hreflang link
                    result.push({
                        rel: 'alternate',
                        hreflang: domain.locale,
                        href,
                    });

                    // If this domain is marked as the default, also add the 'x-default' link
                    if (domain.isDefault) {
                        result.push({
                            rel: 'alternate',
                            hreflang: 'x-default',
                            href,
                        });
                    }
                }
            }
        }

        return result;
    });

    return {
        hreflangLinks,
    };
}
